import {
  Component, Output, EventEmitter, Input, OnInit,
  ElementRef, OnDestroy, ChangeDetectorRef, ViewChildren, QueryList, ViewChild,
  InputSignal,
  input,
} from '@angular/core';
import { FormControl, FormGroup, Validators,
  AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Observable, Subscription } from 'rxjs/index';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { AddressBookI } from '@store/modals';
import { EventLoggerService } from '@services/event-logger-service';
import { ActivatedRoute } from '@angular/router';
import { StringHelperService } from '@services/string-helper/string-helper.service';
import {
  emailRegex, numberRegex, specialCharacterAndEmojiNameRegex,
  specialCharacterAndEmojiRegex,
} from '@shared/address-form/address-validation.constant';
import { AppConfig } from '../../app.config';

const SPECIAL_CHARACTERS: string = 'specialCharacters';
const NUMBERS: string = 'numbers';
const WORD_LIMIT: string = 'wordLimit';
const MIN_CHAR_LIMIT: string = 'minCharLimit';
const MAX_CHAR_LIMIT: string = 'maxCharLimit';

@Component({
  selector: 'address-form',
  templateUrl: './address-form.html',
  standalone: false,
})

export class AddressFormComponent implements OnInit, OnDestroy {
  formDataFromParent: Partial<AddressBookI> = {};
  states: Array<any>;
  isSaveForm: boolean = false;
  addressEditable: boolean;
  pinCodeError: boolean = false;
  error: any = {};
  user: any;
  @Input('locationLoading') locationLoading: boolean = false;
  @Input('prefillAddressShiprocketExperiment') prefillAddressShiprocketExperiment: boolean = false;
  @Input('shiprocketAddressExists') shiprocketAddressExists: boolean = false;
  @Input('shiprocketAddressesCount') shiprocketAddressesCount: number = 0;
  @Input('shiprocketSticky') shiprocketSticky: boolean = true;
  @Output('updateLocationInfo') updateLocationInfo: EventEmitter<void> = new EventEmitter<void>();
  @Input()
  set data(data: { [key: string]: any }) {
    if (this.addressForm) return;
    this.formDataFromParent = { ...data };
    this.formDataFromParent.default = true;
  }
  @Output('dataChange') dataChange: EventEmitter<object> = new EventEmitter<object>();
  @Input()
  set editable(editable: boolean) {
    this.addressEditable = editable;
  }
  @Input('isCheckout') isCheckout: boolean = true;
  @Output('valid') valid: EventEmitter<any> = new EventEmitter();
  // eslint-disable-next-line new-cap
  @ViewChildren('form') form: QueryList<ElementRef>;
  @ViewChild('nameInput') nameInput: ElementRef;
  @ViewChild('numberInput') numberInput: ElementRef;
  @ViewChild('zipCodeInput') zipCodeInput: ElementRef;
  @ViewChild('cityInput') cityInput: ElementRef;
  @ViewChild('buildingDetails') buildingDetails: ElementRef;
  @ViewChild('localityDetails') localityDetails: ElementRef;

  fromReviewOrderPage: InputSignal<boolean> = input();

  addressForm: FormGroup;
  subscriptions: Array<Subscription> = [];
  tags: any[];
  editedCount: number = 0;
  pinCodeMap: any = {};
  experiments: any = {};
  addressBookList$: Observable<AddressBookI[]>;
  hideDefaultAddressSwitch: boolean = false;
  showAlternateNo: boolean = false;
  isCityFetched: boolean = false;
  addressFieldError: any = {
    contactName: { contactNameError: false, errorType: '' },
    city: { cityError: false, errorType: '' },
    buildingDetails: { buildingDetailsError: false, errorType: '' },
    localityDetails: { localityDetailsError: false, errorType: '' },
    landmark: { landmarkError: false, errorType: '' },
  };
  internalUser: boolean = false;

  get buildingPlaceholder(): string {
    return $localize`:@@houseNoBuildingName:House no. / Building name`;
  }

  get localityPlaceholder(): string {
    return $localize`:@@areaRoadName:Area / Road name`;
  }

  get pincodePlaceholder(): string {
    return $localize`:@@sixDigitPincode:6-digit pincode`;
  }

  get cityPlaceholder(): string {
    return $localize`:@@cityTown:City / Town`;
  }

  get landmarkPlaceholder(): string {
    return $localize`:@@nearbyLandmarkOptional:Nearby landmark (optional)`;
  }

  constructor(public conn: ConnectionService,
    public broadcast: BroadcastService,
    private appConfig: AppConfig,
    public commonUtilService: CommonUtilityHelper,
    public eventLoggerService: EventLoggerService,
    public changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private stringHelperService: StringHelperService) {
  }

  async ngOnInit(): Promise<void> {
    this.user = this.conn.getActingUser();
    this.internalUser = this.conn.isInternalUser();
    this.states = this.appConfig.Shared.States;
    // For the first time address entry don't show the switch
    this.hideDefaultAddressSwitch = !!this.route.snapshot.queryParams?.fromCheckout;
    this.fillForm();
    this.subscriptions.push(this.addressForm.valueChanges.subscribe((): void => {
      this.validateForm('', false);
    }));
    this.subscriptions.push(this.addressForm.controls.zipCode.valueChanges.subscribe((zipCode: number): void => {
      if (String(zipCode).length === 6) {
        this.fetchAddressByPincode();
      } else {
        this.isCityFetched = false;
        this.addressForm.controls.city.enable();
      }
      this.updateCityInputState();
    }));
    const initialZipCode = String(this.addressForm.controls.zipCode.value || '');
    if (initialZipCode.length === 6) {
      await this.fetchAddressByPincode();
    }
    await this.fetchAddressBookTags();
  }

  textFieldValidator(
    fieldName: string,
    options?: { minChars?: number; maxWords?: number },
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { value }: { value: string } = control;
      const nonSpaceCount = (value?.match(/\S/g) || []).length;
      const wordCount = value?.split(' ').filter((word: string): boolean => word.trim() !== '').length;

      // Check for special characters or emojis
      if (specialCharacterAndEmojiRegex.test(value)) {
        this.setFieldError(fieldName, SPECIAL_CHARACTERS);
        return { invalidCharacter: true };
      }

      // If a minimum character count is specified, count non-space characters
      if (options?.minChars !== undefined) {
        if (nonSpaceCount < options.minChars) {
          this.setFieldError(fieldName, MIN_CHAR_LIMIT);
          return { message: 'error' };
        }
      }

      // If a maximum word count is specified, check the word count
      if (options?.maxWords !== undefined) {
        if (wordCount > options.maxWords) {
          this.setFieldError(fieldName, WORD_LIMIT);
          return { message: 'error' };
        }
      }

      // Clear any previous errors
      this.clearFieldError(fieldName);
      return null;
    };
  }

  private setFieldError(fieldName: string, errorType: string): void {
    if (this.addressFieldError[fieldName]) {
      this.addressFieldError[fieldName][`${fieldName}Error`] = true;
      this.addressFieldError[fieldName].errorType = errorType;
    }
  }

  private clearFieldError(fieldName: string): void {
    if (this.addressFieldError[fieldName]) {
      this.addressFieldError[fieldName][`${fieldName}Error`] = false;
      this.addressFieldError[fieldName].errorType = '';
    }
  }

  fillForm(): void {
    // eslint-disable-next-line max-len
    this.addressForm = new FormGroup({
      contactName: new FormControl({ value: this.formDataFromParent.contactName, disabled: false },
        { validators: [this.nameCheckValidator(), Validators.minLength(3)] }),
      userEmail: new FormControl({ value: this.formDataFromParent.userEmail, disabled: false },
        { validators: [Validators.email, Validators.pattern(emailRegex)] }),
      mobileNumber: new FormControl({ value: this.formDataFromParent?.mobileNumber || this.user?.get('MobileNumber'), disabled: false },
        { validators: [Validators.min(1000000000), Validators.max(9999999999)] }),
      alternateNumber: new FormControl({ value: this.formDataFromParent.alternateNumber, disabled: false },
        { validators: [Validators.min(1000000000), Validators.max(9999999999)] }),
      zipCode: new FormControl({ value: this.formDataFromParent.zipCode, disabled: false },
        { validators: [Validators.max(999999), Validators.required] }),
      buildingDetails: new FormControl({
        value: this.stringHelperService.removeSubstringFromEnd(
          this.formDataFromParent?.buildingDetails || '',
          this.formDataFromParent?.localityDetails || '',
        ),
        disabled: false,
      }, { validators: [this.textFieldValidator('buildingDetails', { minChars: 3 })] }),
      localityDetails: new FormControl({ value: this.formDataFromParent.localityDetails, disabled: false },
        { validators: [this.textFieldValidator('localityDetails', { minChars: 5 })] }),
      landmark: new FormControl({ value: this.formDataFromParent.landmark, disabled: false },
        { validators: [this.textFieldValidator('landmark')] }),
      city: new FormControl({ value: this.formDataFromParent.city, disabled: false },
        { validators: [this.textFieldValidator('city', { maxWords: 3 })] }),
      state: new FormControl({ value: this.formDataFromParent.state, disabled: false },
        { validators: [this.textFieldValidator('state')] }),
      tag: new FormControl({ value: this.formDataFromParent.tag, disabled: false },
        { validators: [this.textFieldValidator('tag')] }),
    });
    this.changeDetectorRef.detectChanges();
    const initialZip = String(this.addressForm.controls.zipCode.value || '');
    if (initialZip.length === 6 && !!this.formDataFromParent.city) {
      this.isCityFetched = true;
    }
    this.updateCityInputState();
  }

  async fetchAddressBookTags(): Promise<void> {
    this.tags = JSON.parse(JSON.stringify(await this.conn.fetchAddressTags('address_tag')));
    this.tags?.forEach((tag_: any): void => {
      const tag = tag_;
      tag.default = this.conn.getValueOfLanguageString(tag);
    });
    this.tags.sort((element1: any, element2: any): number => {
      const tagOrder = this.appConfig.Shared.AddressBook.Tags;
      return tagOrder.indexOf(element1.en.toLowerCase()) - tagOrder.indexOf(element2.en.toLowerCase());
    });
    if (!this.formDataFromParent.tag) this.selectTag(0);
  }

  async fetchPincodeInfo(zipCode: number): Promise<any> {
    if (this.pinCodeMap[zipCode]) {
      return this.pinCodeMap[zipCode];
    }
    const { userEmail }: Record<string, AbstractControl<any, any>> = this.addressForm.controls;
    // Reactive form is considering empty email as valid, since it is not a required field.
    // So we need to check for undefined email value
    const isEmailValid = userEmail.status === 'VALID' && !!userEmail.value;
    return this.conn.getPinCodeInfo(zipCode, {
      userEmail: isEmailValid ? 'yes' : '',
    });
  }

  public async fetchAddressByPincode(): Promise<void> {
    const { zipCode }: { zipCode: number } = this.addressForm.getRawValue();
    if (zipCode?.toString().length === 6) {
      const pinCodeData = await this.fetchPincodeInfo(zipCode);
      if (pinCodeData) {
        this.pinCodeMap[zipCode] = pinCodeData;
        this.addressForm.controls.state.setValue(this.getFormatted(pinCodeData?.get('state'), true));
        this.addressForm.controls.city.setValue(this.getFormatted(pinCodeData?.get('district')));
        this.isCityFetched = true;
        this.eventLoggerService.trackEvent('pincode_lookup_success',
          { username: this.user?.get('username'),
            pincode: this.addressForm.controls.zipCode.value });
      } else {
        this.addressForm.controls.city.setValue('');
        this.addressForm.controls.state.setValue('');
        this.isCityFetched = false;
        this.eventLoggerService.trackEvent('pincode_lookup_failed',
          { username: this.user?.get('username'),
            pincode: this.addressForm.controls.zipCode.value });
      }
    }
    this.updateCityInputState();
  }

  async validateForm(from?: string, showError: boolean = true): Promise<void> {
    this.isSaveForm = from === 'parent';
    this.pinCodeError = false;
    let errorFieldSpotted = false;
    let missingField;
    this.constructDeliveryAddress();
    const formData = this.addressForm.getRawValue();
    formData.alternateNumber = Number(formData.alternateNumber);
    this.dataChange.emit({ ...this.formDataFromParent, ...formData });
    this.valid.emit(this.addressForm.valid);
    let pinCodeData: any;
    if (this.addressForm.controls.zipCode.value && this.addressForm.controls.zipCode.value.toString().length > 5) {
      pinCodeData = await this.fetchPincodeInfo(this.addressForm.controls.zipCode.value);
      if (pinCodeData) {
        this.pinCodeError = this.getFormatted(pinCodeData?.get('state'), true) !== this.getFormatted(this.addressForm.controls.state.value, true);
        this.eventLoggerService.trackEvent('pincode_lookup_success',
          { username: this.user?.get('username'),
            pincode: this.addressForm.controls.zipCode.value });
      } else {
        this.pinCodeError = true;
        this.eventLoggerService.trackEvent('pincode_lookup_failed',
          { username: this.user?.get('username'),
            pincode: this.addressForm.controls.zipCode.value });
      }
    } else {
      this.pinCodeError = true;
    }
    if (!showError) return;
    Object.keys(this.addressForm.controls).forEach(async (formKey: string): Promise<void> => {
      const [formElement]: Element[] = this.form.first.nativeElement.querySelectorAll(`[formcontrolname=${formKey}]`);
      if (!formElement || errorFieldSpotted) return;
      if (this.addressForm.controls[formKey].status === 'INVALID') {
        errorFieldSpotted = true;
        formElement.setAttribute('error', '');
        missingField = formElement.nextElementSibling?.innerHTML;
        formElement.parentElement.scrollIntoView({ block: 'center' });
      } else if (formKey === 'zipCode' && this.pinCodeError) {
        formElement.setAttribute('error', '');
      } else {
        formElement.removeAttribute('error');
      }
    });
    if (this.pinCodeError) {
      this.zipCodeInput.nativeElement.focus();
    }
    this.handleIfFormInvalidWithFocus();
  }

  handleIfFormInvalidWithFocus(): void {
    if (!this.addressForm.valid) {
      if (this.addressForm.controls.contactName.status === 'INVALID') {
        this.nameInput.nativeElement.focus();
      } else if (this.addressForm.controls.mobileNumber.status === 'INVALID') {
        this.numberInput.nativeElement.focus();
      } else if (this.addressForm.controls.zipCode.status === 'INVALID') {
        this.zipCodeInput.nativeElement.focus();
      } else if (this.addressForm.controls.city.status === 'INVALID') {
        this.cityInput.nativeElement.focus();
      } else if (this.addressForm.controls.buildingDetails.status === 'INVALID') {
        this.buildingDetails.nativeElement.focus();
      } else if (this.addressForm.controls.localityDetails.status === 'INVALID') {
        this.localityDetails.nativeElement.focus();
      }
    }
  }

  selectTag(tagIndex: number): void {
    this.addressForm.controls.tag.setValue(this.tags[tagIndex]?.default);
  }

  private constructDeliveryAddress(): void {
    const address = [];
    address.push(this.addressForm?.controls.buildingDetails.value);
    address.push(this.addressForm?.controls.localityDetails.value);
    address.push(this.addressForm?.controls.city.value);
    address.push(this.addressForm?.controls.state.value);
    address.push(this.addressForm?.controls.zipCode.value);
    const oldAdress = this.formDataFromParent.deliveryAddress;
    this.formDataFromParent.deliveryAddress = address.filter((each: string): boolean => !!each).join(', ');
    if (oldAdress !== this.formDataFromParent.deliveryAddress) {
      this.editedCount += 1;
    }
  }

  nameCheckValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const fieldName = 'contactName';
      const { value }: any = control;
      const minCharValue = 3;
      const maxCharValue = 25;
      const nonSpaceCount = (value?.match(/\S/g) || []).length;
      const wordCount = value?.split(' ').length;

      const validations = [
        {
          condition: numberRegex.test(value),
          errorConstant: NUMBERS,
          errorResponse: { invalidCharacter: true },
        },
        {
          condition: specialCharacterAndEmojiNameRegex.test(value),
          errorConstant: SPECIAL_CHARACTERS,
          errorResponse: { message: 'error' },
        },
        {
          condition: nonSpaceCount < minCharValue,
          errorConstant: MIN_CHAR_LIMIT,
          errorResponse: { message: 'error' },
        },
        {
          condition: nonSpaceCount > maxCharValue,
          errorConstant: MAX_CHAR_LIMIT,
          errorResponse: { message: 'error' },
        },
        {
          condition: wordCount > 3,
          errorConstant: WORD_LIMIT,
          errorResponse: { message: 'error' },
        },
      ];

      const failedValidation = validations.find((validation: any): void => validation.condition);
      if (failedValidation) {
        this.setFieldError(fieldName, failedValidation.errorConstant);
        return failedValidation.errorResponse;
      }

      this.clearFieldError(fieldName);
      return null;
    };
  }

  sanitizeMobileInput(controlName: string): void {
    const control = this.addressForm.get(controlName);
    if (control) {
      const sanitized = control.value?.toString().replace(/[^0-9]/g, '');
      control.setValue(sanitized, { emitEvent: false });
    }
  }

  getLocationDetails(): any {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'get-pincode' }));
    this.locationLoading = true;
    this.updateLocationInfo.emit();
  }

  /**
   * This method is used to restrict special characters on key press
   */
  restrictSpecialChars(event: any): any {
    const regex = /^[0-9]+$/;
    const key = String.fromCharCode(!event.charCode ? event.which : event.charCode);
    if (!regex.test(key)) {
      event.preventDefault();
    }
  }

  private getFormatted(name: string, isState: boolean = false): string {
    if (!name) return '';
    if (isState && name.length > 15) return this.truncateStateName(name);
    return name.toLowerCase()
      .split(' ')
      .filter((each: string): boolean => !!each?.length)
      .map((each: string): string => `${each[0]}`.toUpperCase() + each.slice(1))
      .join(' ');
  }

  truncateStateName(name: string): string {
    return `${name.split(' ')[0]}...`;
  }

  private updateCityInputState(): void {
    const zipCodeValue = this.addressForm.controls.zipCode.value?.toString() || '';
    if (zipCodeValue.length === 6 && this.isCityFetched) {
      this.addressForm.controls.city.disable();
    } else {
      this.addressForm.controls.city.enable();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
  }
}
